import type { InjectionProtocol } from "../types";
import { ALL_PROTOCOL_PAIRINGS } from "../pairings";
import { BEMFOLA_PROTOCOL } from "./bemfola";
import { MENOPUR_MULTIDOSE_PROTOCOLS, MENOPUR_PROTOCOLS } from "./menopur";
import { PUREGON_PROTOCOL } from "./puregon";
import { CETROTIDE_PROTOCOL } from "./cetrotide";
import { DALTEPARIN_PROTOCOL } from "./dalteparin";
import { TINZAPARIN_PROTOCOL } from "./tinzaparin";
import { DECAPEPTYL_PROTOCOL } from "./decapeptyl";
import { ELONVA_PROTOCOL } from "./elonva";
import { ENOXAPARIN_PROTOCOL } from "./enoxaparin";
import { FOSTIMON_PROTOCOL } from "./fostimon";
import { LUBION_PROTOCOLS } from "./lubion";
import { LUVERIS_PROTOCOL } from "./luveris";
import { MOUNJARO_PROTOCOLS } from "./mounjaro";
import { WEGOVY_PROTOCOLS } from "./wegovy";
import { VICTOZA_PROTOCOL } from "./victoza";
import { ZEPBOUND_PROTOCOLS } from "./zepbound";
import { GONAL_F_PROTOCOLS } from "./gonal-f";
import { LUPRON_PROTOCOL } from "./lupron";
import { ORGALUTRAN_PROTOCOL } from "./orgalutran";
import { OZEMPIC_PROTOCOL } from "./ozempic";
import { OVIDREL_PROTOCOLS } from "./ovidrel";
import { PERGOVERIS_PROTOCOLS } from "./pergoveris";
import { PREGNYL_PROTOCOLS } from "./pregnyl";
import { REKOVELLE_PROTOCOL } from "./rekovelle";
import { SAXENDA_PROTOCOL } from "./saxenda";

/**
 * Every procedure the coach can walk today.
 *
 * The registry is the iteration order made concrete: a medication is coachable
 * exactly when its protocol appears here, and adding the next one is a single
 * line. Ovidrel/Ovitrelle first because it is one fixed dose with no titration
 * and no missed-dose path; the order after it runs by how contained the device
 * is rather than how common the drug is, so the engine meets a multi-vial
 * reconstitution before it is trusted with one.
 *
 * See `docs/features/injection-coach/README.md#suggested-order`.
 */
export const ALL_INJECTION_PROTOCOLS: readonly InjectionProtocol[] = [
  ...OVIDREL_PROTOCOLS,
  // Urinary hCG: the same trigger, and the first procedure with a vial to
  // reconstitute, a needle to change and — on the route the label names — an
  // intramuscular site the patient cannot see.
  ...PREGNYL_PROTOCOLS,
  // The GnRH antagonists, added as a pair because the pair is the point: they
  // do the same job at the same site on the same schedule and differ only by
  // device, so one of them is a kit to mix and the other is ready to inject.
  CETROTIDE_PROTOCOL,
  ORGALUTRAN_PROTOCOL,
  // Leuprolide, which is a suppression drug and a trigger depending only on its
  // dose — so it is offered from a medication row and from the hCG walk it can
  // be paired with, and never from the trigger step on its own.
  LUPRON_PROTOCOL,
  // Follitropin alfa: the first medication here that is the cycle rather than a
  // door held shut around it, the one a patient gives most often, and the first
  // whose dose her clinic changes mid-course. Two devices, because a pen and a
  // vial share a molecule and nothing else.
  ...GONAL_F_PROTOCOLS,
  // The same molecule under another name, and another device. It answered to
  // Gonal-F's walk until 2026-09-28, which told a Bemfola patient to keep a
  // single-use pen for 28 days. ANSM sets out exactly these categories and
  // records that dose errors are already happening, while permitting the
  // pharmacy to substitute one for another mid-protocol.
  //
  // Ovaleap, the third, is deliberately absent. Its EMA product information
  // names no site, no needle angle and no hold time, and defers to a pen
  // instructions-for-use that is not published anywhere we can read. Writing
  // its walk meant writing a procedure, which is the one thing a protocol may
  // not do, so an Ovaleap patient reaches the factsheet and no walk.
  BEMFOLA_PROTOCOL,
  // Follitropin beta, and the first pen here that is not prefilled: the
  // cartridge is the medicine and the pen is a separate object she keeps, sold
  // apart from it. Both labels agree it comes no other way — the EMA product
  // information names no vial at all — so the two other procedures its content
  // still carries are offered by nothing.
  PUREGON_PROTOCOL,
  // Menotropins, and the first medication whose numbers differ by market rather
  // than whose device does: the US label permits six vials in one millilitre
  // and subcutaneous use only, the European one permits three and both routes.
  // Two walks, each restricted to its own markets, neither averaging the other.
  ...MENOPUR_PROTOCOLS,
  // And the presentation those two are not for. Menopur 600 and 1200 IU are
  // multidose: reconstituted once, then kept and drawn from for up to 28 days.
  // Two walks again, and this time the axis is the evening rather than the
  // market — the first mixes, the rest only draw.
  ...MENOPUR_MULTIDOSE_PROTOCOLS,
  // Corifollitropin alfa: the stimulation medication given once instead of
  // daily. A prefilled syringe with a needle to screw on, a
  // droplet to check, and a rule no other procedure has — never a second time
  // in the same cycle.
  ELONVA_PROTOCOL,
  // Follitropin delta: the pen dosed in micrograms, primed once, with a dial
  // that stops at 20 while a dose may be more — the one pen whose label spells
  // out the split dose.
  REKOVELLE_PROTOCOL,
  // Follitropin alfa with lutropin alfa in one pen or one powder vial: the
  // first medication here sold as both a pen to dial and a kit to mix, so the
  // device question has to offer a vial for the first time.
  ...PERGOVERIS_PROTOCOLS,
  // Triptorelin 0.1 mg, the daily agonist of the French long protocol: a
  // powder vial and a glass ampoule of solvent, and a label that states the
  // route, the solvent and the missed dose, and nothing about the hands.
  DECAPEPTYL_PROTOCOL,
  // Enoxaparin: not a fertility drug, but prescribed through a cycle for
  // months, and the one syringe whose air bubble is part of the dose — the
  // third contradictory bubble rule in the catalogue, now guided.
  ENOXAPARIN_PROTOCOL,
  // Urofollitropin: the purified urinary FSH still sold as a powder vial, one
  // per 75 or 150 units, chained into one syringe for a bigger dose — and the
  // one leaflet here that says to massage the site rather than not to.
  FOSTIMON_PROTOCOL,
  // Subcutaneous progesterone, daily into the twelfth week of a pregnancy: a
  // prefilled syringe and a vial to draw up, two walks from one leaflet in two
  // editions — and the first walk here that outlives the cycle.
  ...LUBION_PROTOCOLS,
  // Lutropin alfa, the recombinant LH injected daily beside her FSH: a powder
  // vial and a solvent vial, up to three powders in one syringe — until now a
  // name aliased on the Pergoveris entry, now a walk of its own.
  LUVERIS_PROTOCOL,
  // Dalteparin, the other low-molecular-weight heparin: the same kept air
  // bubble as enoxaparin, and a needle she bends into a plastic trap herself.
  DALTEPARIN_PROTOCOL,
  // Tinzaparin, Innohep: the third low-molecular-weight heparin, the bubble
  // kept unless the dose is less than the syringe, an orange safety device
  // bent back over the needle on a table.
  TINZAPARIN_PROTOCOL,
  // Semaglutide, the weekly dial-a-dose pen of the months before a cycle:
  // the first pen here whose label checks the flow of every new pen and
  // counts to six — not a fertility drug, and never on a cycle step.
  OZEMPIC_PROTOCOL,
  // Tirzepatide, two pens: the KwikPen with four fixed weekly doses, a prime
  // before every injection and a count of five, and the single-dose pen with
  // a hidden needle; the coach asks which box. Metabolic, never a trigger.
  ...MOUNJARO_PROTOCOLS,
  // Semaglutide at weight-management doses, two pens: Ozempic's FlexTouch
  // hands with five strengths, and a single-dose pen with a hidden needle
  // and a yellow bar; the coach asks which box. Metabolic, never a trigger.
  ...WEGOVY_PROTOCOLS,
  // Tirzepatide on the US market, four boxes from the FDA label: two pens and
  // two vials, each pair told apart by the box question. Walked for the US
  // side only; the French is the EMA Mounjaro notice's where it corresponds.
  ...ZEPBOUND_PROTOCOLS,
  // Liraglutide, the daily dial-a-dose pen: Ozempic's hands with a number she
  // dials, and a twelve-hour rule; metabolic, never a trigger.
  SAXENDA_PROTOCOL,
  // Liraglutide for diabetes, the Saxenda pen with three doses to dial and
  // a flow check tried four times; metabolic, never a trigger.
  VICTOZA_PROTOCOL,
];

/** Every trade name any protocol covers, lowercased for matching. */
export const COACHABLE_BRAND_NAMES: readonly string[] =
  ALL_INJECTION_PROTOCOLS.flatMap((protocol) =>
    protocol.brands.map((brand) => brand.name.toLowerCase()),
  );

/**
 * Every name a prescription may carry for a procedure the coach knows.
 *
 * Trade names and molecule names both, because a clinic records either and a
 * patient chose neither. This is what decides whether the coach is *offered*;
 * what it then *calls* the medication is a different question with a stricter
 * answer — see `coachNameFromPrescribedName`.
 */
export const COACHABLE_NAMES: readonly string[] = [
  ...COACHABLE_BRAND_NAMES,
  ...ALL_INJECTION_PROTOCOLS.flatMap((protocol) =>
    (protocol.aliases ?? []).map((alias) => alias.toLowerCase()),
  ),
  // And the names of prescriptions that are two medications at one hour. They
  // have no procedure of their own — they are taken apart into two that do —
  // but a patient can log one, and the coach has an answer for her.
  ...ALL_PROTOCOL_PAIRINGS.flatMap((pairing) =>
    pairing.names.map((name) => name.toLowerCase()),
  ),
];
